/**
 * P4 — competing values between DOCUMENTS are preserved, never resolved by
 * order of arrival.
 *
 * The bootstrap's multi-document fill only ever wrote the still-missing
 * fields, so when a second questionnaire gave a different average flow the
 * figure was dropped on the floor and the report saw one number. These
 * helpers compare two documents' extracted payloads leaf by leaf and return
 * the disagreements, each value carrying the document and the attribution
 * (who said it, in what role, when, for which project) it came from.
 *
 * Pure; no I/O. The bootstrap wires the results onto
 * `EvidenceSection.docConflicts`; `sedToText` renders them so the report
 * says both values and why they differ.
 */
import type { DocEvidenceConflict, DocEvidenceConflictValue, EvidenceAttribution } from '../../types/siteEvidence';

export interface DocValueWitness {
  docId: string;
  docName: string;
  data: Record<string, unknown> | null | undefined;
  attribution?: EvidenceAttribution;
  pageNumber?: number;
  excerptPreview?: string;
}

export interface DetectDocValueConflictsOptions {
  /** Relative tolerance for numbers (default 2%): |a-b| ≤ tol × max(|a|,|b|) is agreement. */
  relativeTolerance?: number;
  /** Stamp for `detectedAt`; defaults to now. */
  now?: string;
}

type Leaf = string | number | boolean;

/** Keys that describe provenance or lineage rather than the fact itself. */
const IGNORED_KEYS = new Set([
  'provenance', 'source', 'sourceUrl', 'retrievedAt', 'dataAsOf', 'disclaimer', 'notes', 'basis',
  'statedBy', 'statedByRole', 'organisation', 'statedOn', 'appliesToProject', 'projectName',
  'pageNumber', 'sectionHeading', 'excerptPreview',
]);

/** Flatten an object to dotted leaf paths; provenance keys and arrays are skipped. */
export function flattenLeafValues(data: unknown, prefix = ''): Record<string, Leaf> {
  const out: Record<string, Leaf> = {};
  if (!data || typeof data !== 'object' || Array.isArray(data)) return out;
  for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
    if (IGNORED_KEYS.has(key)) continue;
    const path = prefix ? `${prefix}.${key}` : key;
    if (value === null || value === undefined) continue;
    if (Array.isArray(value)) continue;
    if (typeof value === 'object') {
      Object.assign(out, flattenLeafValues(value, path));
      continue;
    }
    if (typeof value === 'number') {
      if (Number.isFinite(value)) out[path] = value;
      continue;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) out[path] = trimmed;
      continue;
    }
    if (typeof value === 'boolean') out[path] = value;
  }
  return out;
}

/** True when two leaf values disagree beyond tolerance (numbers) or textually (strings). */
export function leafValuesDiffer(a: Leaf, b: Leaf, relativeTolerance = 0.02): boolean {
  if (typeof a === 'number' && typeof b === 'number') {
    const scale = Math.max(Math.abs(a), Math.abs(b));
    if (scale === 0) return false;
    return Math.abs(a - b) > relativeTolerance * scale;
  }
  if (typeof a === 'number' || typeof b === 'number') {
    const na = typeof a === 'number' ? a : Number(String(a).replace(/[,\s]/g, ''));
    const nb = typeof b === 'number' ? b : Number(String(b).replace(/[,\s]/g, ''));
    if (Number.isFinite(na) && Number.isFinite(nb)) return leafValuesDiffer(na, nb, relativeTolerance);
    return true;
  }
  if (typeof a === 'boolean' || typeof b === 'boolean') return a !== b;
  return a.replace(/\s+/g, ' ').toLowerCase() !== b.replace(/\s+/g, ' ').toLowerCase();
}

function witnessValue(witness: DocValueWitness, value: Leaf): DocEvidenceConflictValue {
  return {
    value,
    docId: witness.docId,
    docName: witness.docName,
    ...(witness.attribution ? { attribution: witness.attribution } : {}),
    ...(typeof witness.pageNumber === 'number' ? { pageNumber: witness.pageNumber } : {}),
    ...(witness.excerptPreview ? { excerptPreview: witness.excerptPreview } : {}),
  };
}

function conflictNote(a: DocValueWitness, b: DocValueWitness): string | undefined {
  const aOther = a.attribution?.appliesToProject === 'other-project';
  const bOther = b.attribution?.appliesToProject === 'other-project';
  if (aOther !== bOther) {
    const other = aOther ? a : b;
    return `The value from "${other.docName}" was prepared for another project${other.attribution?.projectName ? ` (${other.attribution.projectName})` : ''}; say so rather than treating it as this site's figure.`;
  }
  if (a.attribution?.statedOn && b.attribution?.statedOn && a.attribution.statedOn !== b.attribution.statedOn) {
    return `The two statements carry different dates (${a.attribution.statedOn} vs ${b.attribution.statedOn}); the later one may supersede, but report both.`;
  }
  return undefined;
}

/**
 * Compare two documents' payloads for the same section. Only leaves present
 * in BOTH count; a value one document lacks is a gap, not a conflict.
 */
export function detectDocValueConflicts(
  primary: DocValueWitness,
  secondary: DocValueWitness,
  opts: DetectDocValueConflictsOptions = {},
): DocEvidenceConflict[] {
  if (primary.docId === secondary.docId) return [];
  const a = flattenLeafValues(primary.data);
  const b = flattenLeafValues(secondary.data);
  const out: DocEvidenceConflict[] = [];
  const now = opts.now ?? new Date().toISOString();
  const note = conflictNote(primary, secondary);
  for (const [path, aValue] of Object.entries(a)) {
    const bValue = b[path];
    if (bValue === undefined) continue;
    if (!leafValuesDiffer(aValue, bValue, opts.relativeTolerance)) continue;
    out.push({
      field: path,
      status: 'unresolved',
      detectedAt: now,
      values: [witnessValue(primary, aValue), witnessValue(secondary, bValue)],
      ...(note ? { note } : {}),
    });
  }
  return out;
}

/**
 * Compare a slot whose top-level fields may each have come from a different
 * document against a new document's payload. `ownerOf(field)` names the
 * witness that supplied the slot's current value for that field.
 */
export function detectSlotDocConflicts(
  fields: ReadonlyArray<string>,
  slotData: Record<string, unknown> | null | undefined,
  ownerOf: (field: string) => DocValueWitness,
  incoming: DocValueWitness,
  opts: DetectDocValueConflictsOptions = {},
): DocEvidenceConflict[] {
  if (!slotData || !incoming.data) return [];
  const out: DocEvidenceConflict[] = [];
  for (const field of fields) {
    const current = slotData[field];
    const candidate = incoming.data[field];
    if (current === null || current === undefined || candidate === null || candidate === undefined) continue;
    const owner = ownerOf(field);
    out.push(...detectDocValueConflicts(
      { ...owner, data: { [field]: current } },
      { ...incoming, data: { [field]: candidate } },
      opts,
    ));
  }
  return out;
}

function conflictKey(c: DocEvidenceConflict): string {
  return `${c.field}::${c.values.map(v => v.docId).sort().join('|')}`;
}

/** Merge new conflicts into the slot's list; a re-detected pair replaces its earlier row. */
export function mergeDocConflicts(
  existing: ReadonlyArray<DocEvidenceConflict> | undefined,
  incoming: ReadonlyArray<DocEvidenceConflict>,
): DocEvidenceConflict[] {
  const byKey = new Map<string, DocEvidenceConflict>();
  for (const c of existing ?? []) byKey.set(conflictKey(c), c);
  for (const c of incoming) byKey.set(conflictKey(c), c);
  return [...byKey.values()];
}
